// ============================================================================
// usePermissions Hook
// ============================================================================

import { useMemo } from 'react';
import { useApp } from '../contexts/AuthContext';
import { getPermissions, canPerformAction, getAllowedActions } from '../utils/permissions';
import { UserRole } from '../types';

export const usePermissions = () => {
  const { user } = useApp();

  const permissions = useMemo(() => {
    if (!user) {
      return {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canApprove: false,
        canExport: false,
        canManageUsers: false,
      };
    }
    return getPermissions(user.role);
  }, [user]);

  const can = (action: keyof ReturnType<typeof getPermissions>): boolean => {
    if (!user) return false;
    return canPerformAction(user.role, action);
  };

  const canEditEntry = (entryStatus: string, entryOwnerId?: string): boolean => {
    if (!user) return false;

    // Admin can edit anything except approved entries
    if (user.role === 'Admin') {
      return entryStatus !== 'approved';
    }

    // Contributors can only edit their own draft entries
    if (user.role === 'Contributor') {
      return entryStatus === 'draft' && entryOwnerId === user.id;
    }

    return false;
  };

  const canDeleteEntry = (entryStatus: string, entryOwnerId?: string): boolean => {
    if (!user) return false;

    // Admin can delete any entry, including approved ones
    return user.role === 'Admin';
  };

  const getEntryActions = (entryStatus: string, entryOwnerId?: string) => {
    if (!user) {
      return {
        canEdit: false,
        canDelete: false,
        canApprove: false,
        canReject: false,
      };
    }

    return getAllowedActions(user.role, user.id, entryStatus, entryOwnerId);
  };

  return {
    ...permissions,
    can,
    canEditEntry,
    canDeleteEntry,
    getEntryActions,
    userRole: user?.role || null,
    userId: user?.id || null,
  };
};
